import type { SearchSourceType } from '@affine/core/modules/docs-search';

import type {
  DocsSearchBlockStatus,
  DocsSearchResult,
} from '../../docs-search/services/source-navigation';

const LOADED_BLOCK_REQUIRED_SOURCE_TYPES: ReadonlySet<SearchSourceType> =
  new Set(['attachment', 'database', 'embed', 'image', 'text']);

export type StaleQuickSearchResult = {
  blockId: string;
  docId: string;
  query: string;
  sourceType: SearchSourceType;
};

export function requiresLoadedBlockForQuickSearchResult(
  result: Pick<DocsSearchResult, 'blockId' | 'sourceType'>
): result is Pick<DocsSearchResult, 'sourceType'> & { blockId: string } {
  return (
    !!result.blockId &&
    LOADED_BLOCK_REQUIRED_SOURCE_TYPES.has(result.sourceType)
  );
}

export function filterStaleLoadedBlockResults(
  query: string,
  results: readonly DocsSearchResult[],
  options: {
    onStaleResult?: (result: StaleQuickSearchResult) => void;
    resolveBlockStatus: (
      docId: string,
      blockId: string
    ) => DocsSearchBlockStatus;
  }
): DocsSearchResult[] {
  return results.filter(result => {
    if (!requiresLoadedBlockForQuickSearchResult(result)) {
      return true;
    }

    const status = options.resolveBlockStatus(result.docId, result.blockId);
    if (status !== 'missing') {
      return true;
    }

    options.onStaleResult?.({
      blockId: result.blockId,
      docId: result.docId,
      query,
      sourceType: result.sourceType,
    });
    return false;
  });
}

export type DedupedStaleResultNudge = {
  dispose: () => void;
  nudge: (result: StaleQuickSearchResult) => void;
};

export function createDedupedStaleResultNudge(options: {
  addPriority: (docId: string, priority: number) => () => void;
  priority?: number;
  warn?: (result: StaleQuickSearchResult) => void;
}): DedupedStaleResultNudge {
  const priority = options.priority ?? 10;
  const undoByDocId = new Map<string, () => void>();

  return {
    dispose: () => {
      for (const undo of undoByDocId.values()) {
        undo();
      }
      undoByDocId.clear();
    },
    nudge: result => {
      if (undoByDocId.has(result.docId)) {
        return;
      }

      undoByDocId.set(
        result.docId,
        options.addPriority(result.docId, priority)
      );
      options.warn?.(result);
    },
  };
}
